import { getInvalidFileIndexes, validateFileAmount, validateFileSize } from 'utils/validation'
import { DEFAULT_FILE_SIZE_LIMIT } from 'constants/constants'
import { useTranslation } from 'next-i18next/pages'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { FileUpload as DSFileUpload } from '@boltenergy-be/design-system'
import { useGetLanguage } from 'hooks/useGetLanguage'
import { getFieldError, getFieldName } from 'components/inputs/utils'
import { FileUploadProps } from './types'
import { useMemo } from 'react'

const FileUpload = ({ accept, required, parentDotPath, identifier, label, limit, grid, maxAmountFiles, ...props }: FileUploadProps) => {
  // i18n
  const { t } = useTranslation(['commonFormFields', 'validation', 'common'])
  const language = useGetLanguage('uppercase')

  // React Hook Form
  const { control, formState } = useFormContext()

  // Constants
  const fieldKey = getFieldName(identifier, parentDotPath)
  const fieldError = getFieldError(identifier, parentDotPath, formState.errors)
  const effectiveLimit = limit ?? DEFAULT_FILE_SIZE_LIMIT

  // Derive invalid file indexes from the watched field value so erroredFileIndex stays in sync without side effects
  const watchedFiles = useWatch({ control, name: fieldKey }) as FileList | undefined
  const invalidIndexes = useMemo(
    () => (watchedFiles && fieldError?.type === 'validate' ? getInvalidFileIndexes(watchedFiles, effectiveLimit) : []),
    [watchedFiles, fieldError?.type, effectiveLimit]
  )

  return (
    <Controller
      {...{ control }}
      name={fieldKey}
      rules={{
        required: required ? t('validation:required') : false,
        validate: {
          fileSize: (value) => validateFileSize(value, effectiveLimit, language, t),
          fileAmount: (value) => (maxAmountFiles !== undefined && props.multiple ? validateFileAmount(value, maxAmountFiles, t) : true)
        }
      }}
      render={({ field: { onChange, value }, fieldState }) => {
        return (
          <DSFileUpload
            {...props}
            accept={accept || 'application/pdf, image/jpeg, image/png, image/heic, image/heif'}
            error={fieldState?.error?.message}
            erroredFileIndex={fieldState?.error ? invalidIndexes : undefined}
            files={value}
            grid={grid || 'full'}
            label={label || t('common:chooseFile')}
            language={language}
            limit={effectiveLimit}
            onChange={(e) => onChange(e.target.files)}
            removeUploadedFile={(newFileList) => onChange(newFileList)}
            {...(!required && { optionalText: t('common:optional') })}
          />
        )
      }}
    />
  )
}

export default FileUpload
